import { getStorage, ref } from "firebase/storage";
import { useMemo, useState } from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { useNavigate, useParams } from "react-router-dom";
import getFileIconCss from "../../modules/fileIcons";
import Delete from "../../components/documents/delete";
import Alert from "react-bootstrap/Alert";
import Create from "../../components/documents/add";
import { useAuthState } from "react-firebase-hooks/auth";
import { getAuth } from "firebase/auth";
import useListAll from "../../hooks/useListAll";
import { doc, getFirestore } from "firebase/firestore";
import { getFirestoreZodConverter } from "../../modules/firestoreZodConverter";
import { ProjectRole, projectDocumentSchema, userDocumentSchema } from "../../types/firestore";
import { useDocumentData } from "react-firebase-hooks/firestore";
import LoadingAlert from "../../components/common/loadingAlert";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import useSearch from "../../hooks/useSearch";

const auth = getAuth();
const storage = getStorage();
const firestore = getFirestore();

function splitFilename(filenameWithExtension: string) {
	const splitFilename = filenameWithExtension.split(".");

	const extension = splitFilename.pop();

	const filename = splitFilename.join(".");

	return {
		filename,
		extension,
	};
}

export default function DocumentsList() {
	const navigate = useNavigate();

	const [search, setSearch] = useState("");

	const [user, loading] = useAuthState(auth);

	if (!user && !loading) {
		navigate("/account");
	}

	const { projectId, userId } = useParams();
	if (!projectId) throw "DocumentsList page requires a projectId parameter.";

	const documentsRef = useMemo(
		() =>
			ref(
				storage,
				userId ? `Documents/${projectId}/Users/${userId}` : `Documents/${projectId}/Project`,
			),
		[projectId, userId],
	);

	const [documentsList, , documentsListError, reloadDocumentsList] = useListAll(documentsRef);

	const documentsSearch = useSearch(documentsList?.items ?? [], search, {
		keys: ["name"],
	});

	const projectDocumentRef = useMemo(
		() =>
			doc(firestore, "Projects", projectId).withConverter(
				getFirestoreZodConverter(projectDocumentSchema),
			),
		[projectId],
	);

	const userDocumentRef = useMemo(
		() =>
			userId
				? doc(firestore, "Users", userId).withConverter(
						getFirestoreZodConverter(userDocumentSchema),
				  )
				: undefined,
		[userId],
	);

	const [projectDocument, , projectDocumentError] = useDocumentData(projectDocumentRef);

	const [userDocument, , userDocumentError] = useDocumentData(userDocumentRef);

	if (!user || !documentsList || !projectDocument || (userId && !userDocument)) {
		return <LoadingAlert error={documentsListError || projectDocumentError || userDocumentError} />;
	}

	const role = projectDocument.Members[user.uid];

	const isAdmin = role === ProjectRole.Owner || role === ProjectRole.Admin;

	return (
		<div>
			<div className="mb-2">
				{userId ? (
					<b>
						Dokumente von {userDocument?.FirstName} {userDocument?.LastName}
					</b>
				) : (
					<b>Projektdokumente</b>
				)}
			</div>

			<FloatingLabel controlId="floatingInput" label="Suchen" className="mb-2">
				<Form.Control
					type="email"
					placeholder="Suchen"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
			</FloatingLabel>

			<Card role="button" className="mb-2 clickable-card" onClick={() => navigate(-1)}>
				<Card.Body>
					<Row>
						<Col xs="auto">
							<i className="bi bi-arrow-left-circle fs-1" />
						</Col>
						<Col className="d-flex align-items-center">Zurück</Col>
					</Row>
				</Card.Body>
			</Card>

			{isAdmin && (
				<Create
					folderPath={documentsRef.fullPath}
					onAdded={() => {
						reloadDocumentsList();
					}}
				/>
			)}

			{documentsSearch.length > 0 ? (
				documentsSearch.map((document) => (
					<Card
						key={document.name}
						role="button"
						className="mb-2 clickable-card"
						onClick={() => {
							if (userId) {
								navigate(`/projects/${projectId}/view/Users/${userId}/${document.name}`);
							} else {
								navigate(`/projects/${projectId}/view/Project/${document.name}`);
							}
						}}>
						<Card.Body>
							<Row>
								<Col xs="auto">
									<i
										className={
											getFileIconCss(splitFilename(document.name).extension ?? "") + " bi fs-1"
										}
									/>
								</Col>
								<Col className="d-flex align-items-center">
									{splitFilename(document.name).filename}
								</Col>
								{isAdmin && (
									<Col xs="auto">
										<Delete
											documentPath={document.fullPath}
											onDeleted={() => {
												reloadDocumentsList();
											}}
										/>
									</Col>
								)}
							</Row>
						</Card.Body>
					</Card>
				))
			) : (
				<Alert variant="warning">Keine Dokumente gefunden.</Alert>
			)}
		</div>
	);
}
